import { gql } from '@apollo/client';
import type { DropdownField, InputField } from '@snapchat/mw-contentful-schema';
import type { BackgroundColor } from '@snapchat/snap-design-system-marketing';

import { fragments as analyticsFragments } from '../../../../components/Analytics';
import type { AnalyticsDataProps } from '../../../../components/Analytics/types';
import type { BlockDataProps } from '../../../../components/Block/query';
import { blockAllFragment } from '../../../../components/Block/query';
import type { MediaDataProps } from '../../../../components/Media';
import { fragments as assetsFragments } from '../../../../components/Media';
import type { ContentfulSysProps } from '../../../../types/contentful';
import type { RichText } from '../../../../types/RichText';
import { contentfulSysIdFragment } from '../../../../utils/contentful/contentfulQuery';

export interface ArDownloadFormBlockQuery {
  arDownloadFormBlock: ArDownloadFormBlockDataProps;
}

export interface ArDownloadFormBlockDataProps extends ContentfulSysProps {
  title?: RichText | string;
  supertitle?: RichText | string;
  subtitle?: RichText | string;
  backgroundColor: BackgroundColor;
  endpoint: string;
  eula: InputField;
  version: InputField;
  platform: DropdownField;
  analytics: AnalyticsDataProps;
  submitText: string;
  submitSuccessText?: string;
  backgroundMedia?: MediaDataProps;
  backgroundMediaMobile?: MediaDataProps;
  logo?: MediaDataProps;
  successBlock: BlockDataProps;
  supportedPlatforms: Array<{ name: string; isSupported: boolean }>;
  supportedPlatformsLabel?: RichText | string;
  releaseNotesLinkText?: RichText | string;
  isLatestVersion?: boolean;
}

export const arDownloadFormBlockFragment = {
  all: gql`
    fragment ArDownloadFormBlockAll on ArDownloadFormBlock {
      ...ContentfulSysId
      title {
        json
      }
      supertitle {
        json
      }
      subtitle {
        json
      }
      backgroundColor
      backgroundMedia {
        ...AssetAll
      }
      backgroundMediaMobile {
        ...AssetAll
      }
      logo {
        ...AssetAll
      }
      endpoint
      submitText
      submitSuccessText
      successBlock {
        ...BlockAll
      }
      analytics {
        ...AnalyticsAll
      }
      eula {
        ...ContentfulSysId
        name
        type
        label
        richLabel {
          json
        }
        initialValue
        shouldResetToInitial
        placeholder
        required
        validation
        helpText
        error {
          json
        }
      }
      version {
        ...ContentfulSysId
        name
        type
        label
        initialValue
      }
      supportedPlatforms
      supportedPlatformsLabel {
        json
      }
      releaseNotesLinkText {
        json
      }
      isLatestVersion
      platform {
        ...ContentfulSysId
        name
        optionsCollection {
          items {
            ... on DropdownFieldOption {
              ...ContentfulSysId
              key
              value
            }
          }
        }
        initialOption {
          ... on DropdownFieldOption {
            ...ContentfulSysId
            key
            value
          }
        }
        shouldResetToInitial
        error {
          json
        }
      }
    }
    ${contentfulSysIdFragment}
    ${assetsFragments.all}
    ${analyticsFragments.all}
    ${blockAllFragment}
  `,
};

export const query = gql`
  query ARDownloadFormBlockQuery($id: String!, $preview: Boolean!, $locale: String!) {
    arDownloadFormBlock(id: $id, preview: $preview, locale: $locale) {
      ...ArDownloadFormBlockAll
    }
  }
  ${arDownloadFormBlockFragment.all}
`;
